import { GroupDTO, CreateGroupRequest, UpdateGroupRequest } from '@/models';
import { useGroupStore, useInstitutionStore, useUIStore } from '@/store';
import React, { useCallback, useEffect, useState } from 'react';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 10;

/**
 * Manages group CRUD operations, pagination, and modal state
 * for the Group Management page
 */
export const useGroupManagement = () => {
  const {
    groups,
    pagination,
    isLoading,
    error,
    fetchGroups,
    createGroup,
    updateGroup,
    deleteGroup,
    clearError,
  } = useGroupStore();

  const { institutions, fetchInstitutions } = useInstitutionStore();
  const { addNotification } = useUIStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedGroup, setSelectedGroup] = useState<GroupDTO | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<GroupDTO | null>(null);
  const [formData, setFormData] = useState<CreateGroupRequest>({
    institutionId: '',
    name: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    phoneNumber: '',
    email: '',
    contact: '',
    accountNumber: '',
    safetyVideoUrl: '',
    emergencyContactMessage: '',
    certificationConfiguration: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchGroups(currentPage, pageSize);
    fetchInstitutions(1, 100);
  }, [currentPage, pageSize, fetchGroups, fetchInstitutions]);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  }, []);

  const handleCreateClick = useCallback(() => {
    setModalMode('create');
    setSelectedGroup(null);
    setFormData({
      institutionId: '',
      name: '',
      address: '',
      city: '',
      province: '',
      postalCode: '',
      phoneNumber: '',
      email: '',
      contact: '',
      accountNumber: '',
      safetyVideoUrl: '',
      emergencyContactMessage: '',
      certificationConfiguration: '',
    });
    setShowModal(true);
    clearError();
  }, [clearError]);

  const handleEditClick = useCallback(
    (group: GroupDTO) => {
      setModalMode('edit');
      setSelectedGroup(group);
      setFormData({
        institutionId: group.institutionId,
        name: group.name || '',
        address: group.address || '',
        city: group.city || '',
        province: group.province || '',
        postalCode: group.postalCode || '',
        phoneNumber: group.phoneNumber || '',
        email: group.email || '',
        contact: group.contact || '',
        accountNumber: group.accountNumber || '',
        safetyVideoUrl: group.safetyVideoUrl || '',
        emergencyContactMessage: group.emergencyContactMessage || '',
        certificationConfiguration: group.certificationConfiguration || '',
      });
      setShowModal(true);
      clearError();
    },
    [clearError]
  );

  const handleDeleteClick = useCallback(
    (group: GroupDTO) => {
      setGroupToDelete(group);
      setShowDeleteModal(true);
      clearError();
    },
    [clearError]
  );

  const handleSubmit = useCallback(async () => {
    setIsSaving(true);
    try {
      if (modalMode === 'create') {
        await createGroup(formData);
        addNotification({
          type: 'success',
          message: 'Group created successfully',
          duration: 2000,
        });
      } else if (selectedGroup) {
        const updateData: UpdateGroupRequest = {
          institutionId: formData.institutionId,
          name: formData.name,
          address: formData.address,
          city: formData.city,
          province: formData.province,
          postalCode: formData.postalCode,
          phoneNumber: formData.phoneNumber,
          email: formData.email,
          contact: formData.contact,
          accountNumber: formData.accountNumber,
          safetyVideoUrl: formData.safetyVideoUrl,
          emergencyContactMessage: formData.emergencyContactMessage,
          certificationConfiguration: formData.certificationConfiguration,
        };
        await updateGroup(selectedGroup.id, updateData);
        addNotification({
          type: 'success',
          message: 'Group updated successfully',
          duration: 2000,
        });
      }
      setShowModal(false);
      await fetchGroups(currentPage, pageSize);
    } catch (err: unknown) {
      addNotification({
        type: 'error',
        message: (err as Error)?.message || 'Operation failed',
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    modalMode,
    createGroup,
    formData,
    selectedGroup,
    updateGroup,
    fetchGroups,
    currentPage,
    pageSize,
    addNotification,
  ]);

  const handleDelete = useCallback(async () => {
    if (!groupToDelete) return;
    setIsSaving(true);
    try {
      await deleteGroup(groupToDelete.id);
      addNotification({
        type: 'success',
        message: 'Group deleted successfully',
        duration: 2000,
      });
      setShowDeleteModal(false);
      await fetchGroups(currentPage, pageSize);
    } catch (err: unknown) {
      addNotification({
        type: 'error',
        message: (err as Error)?.message || 'Operation failed',
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  }, [groupToDelete, deleteGroup, fetchGroups, currentPage, pageSize, addNotification]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    },
    []
  );

  const closeModal = useCallback(() => setShowModal(false), []);
  const closeDeleteModal = useCallback(() => setShowDeleteModal(false), []);

  return {
    groups,
    institutions,
    pagination,
    isLoading,
    error,
    currentPage,
    pageSize,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    showModal,
    modalMode,
    selectedGroup,
    showDeleteModal,
    groupToDelete,
    formData,
    isSaving,
    setCurrentPage,
    handlePageSizeChange,
    handleCreateClick,
    handleEditClick,
    handleDeleteClick,
    handleSubmit,
    handleDelete,
    handleInputChange,
    closeModal,
    closeDeleteModal,
    clearError,
  };
};
